import { inject, Injectable } from '@angular/core';
import { signalStore, withState, withMethods, } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { IDashboardChartInput, IDashboardState } from 'src/models/dashboard.model';
import dayjs from 'dayjs';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';
import { IChartPointValue, IMoneyPoint } from 'src/models/charts.model';

function parseXTimestamps<T extends IChartPointValue[] | IMoneyPoint[]>(input: T): T {
  return input.map((point) => {
    return {
      ...point,
      x: point.x * 1000, // unix time
    }
  }) as T;
}

enum EDashboardActions {
  BUSINESS_DAYS_REQUEST = '[Dashboard] Business Days Request',
  BUSINESS_DAYS_RESPONSE = '[Dashboard] Business Days Response',
  BUSINESS_DAYS_FAILURE = '[Dashboard] Business Days Failure',
  SUMMARY_REQUEST = '[Dashboard] Summary Request',
  SUMMARY_RESPONSE = '[Dashboard] Summary Response',
  SUMMARY_FAILURE = '[Dashboard] Summary Failure',
  SUMMARY_CHART_REQUEST = '[Dashboard] Summary Chart Request',
  SUMMARY_CHART_RESPONSE = '[Dashboard] Summary Chart Response',
  SUMMARY_CHART_FAILURE = '[Dashboard] Summary Chart Failure',
}

const initialDashboardState: IDashboardState = {
  businessDays: null,
  summary: null,
  summaryChart: null,
};

@Injectable({ providedIn: 'root' })
export class DashboardStore extends signalStore(
  withDevtools('dashboard'),
  withState(initialDashboardState),
  withRequestStatus(),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getBusinessDaysInRange = rxMethod<{ from: dayjs.Dayjs, to: dayjs.Dayjs }>(
      pipe(
        tap(() =>
          updateState(store, EDashboardActions.BUSINESS_DAYS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.DASHBOARD_BUSINESS_DAYS, loadingProps),
          }))
        ),
        switchMap(({ from, to }) =>
          restApiService.getCalendar(from, to).pipe(
            map((response) =>
              updateState(store, EDashboardActions.BUSINESS_DAYS_RESPONSE, ({ loadingProps, errorProps }) => ({
                businessDays: {...response, range: { from, to }},
                ...withoutLoading(ELoadingEntity.DASHBOARD_BUSINESS_DAYS, loadingProps),
                ...withoutError(ELoadingEntity.DASHBOARD_BUSINESS_DAYS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EDashboardActions.BUSINESS_DAYS_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.DASHBOARD_BUSINESS_DAYS, loadingProps),
                  ...withError(ELoadingEntity.DASHBOARD_BUSINESS_DAYS, { error, errorParams: { from, to } }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getDashboardSummary = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EDashboardActions.SUMMARY_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.DASHBOARD_SUMMARY, loadingProps),
          }))
        ),
        switchMap(() =>
          restApiService.getDashboardSummary().pipe(
            map(response =>
              updateState(store, EDashboardActions.SUMMARY_RESPONSE, ({ loadingProps, errorProps }) => ({
                summary: response,
                ...withoutLoading(ELoadingEntity.DASHBOARD_SUMMARY, loadingProps),
                ...withoutError(ELoadingEntity.DASHBOARD_SUMMARY, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EDashboardActions.SUMMARY_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.DASHBOARD_SUMMARY, loadingProps),
                  ...withError(ELoadingEntity.DASHBOARD_SUMMARY, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getDashboardSummaryChart = rxMethod<IDashboardChartInput>(
      pipe(
        tap(() =>
          updateState(store, EDashboardActions.SUMMARY_CHART_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.DASHBOARD_CHART, loadingProps),
          }))
        ),
        switchMap((params) =>
          restApiService.getSummaryChart(params).pipe(
            map(response =>
              updateState(store, EDashboardActions.SUMMARY_CHART_RESPONSE, ({ loadingProps, errorProps }) => ({
                summaryChart: {
                  priceInTime: parseXTimestamps(response.priceInTime),
                  depositsInTime: parseXTimestamps(response.depositsInTime),
                  moneyTransactionsInTime: parseXTimestamps(response.moneyTransactionsInTime),
                },
                ...withoutLoading(ELoadingEntity.DASHBOARD_CHART, loadingProps),
                ...withoutError(ELoadingEntity.DASHBOARD_CHART, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EDashboardActions.SUMMARY_CHART_FAILURE, ({ loadingProps, errorProps }) => ({
                  ...withoutLoading(ELoadingEntity.DASHBOARD_CHART, loadingProps),
                  ...withError(ELoadingEntity.DASHBOARD_CHART, { error, errorParams: {} }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    return {
      getBusinessDaysInRange,
      getDashboardSummary,
      getDashboardSummaryChart,
    };
  })
) {}